import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  X,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingCart,
  Calendar,
  BarChart3,
  Activity,
  Award,
  AlertCircle,
  Loader2,
  Package,
  Download,
  FileText,
  FileSpreadsheet,
  CalendarDays,
  Image,
  Eye,
  EyeOff,
  Clock,
  PieChart,
  Target,
  Star,
  Filter,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";

import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Cell,
  Legend
} from 'recharts';

// Date Range Modal Component
const DateRangeModal = ({ isOpen, onClose, dateRange, setDateRange, onApply }) => {
  const [tempRange, setTempRange] = useState(dateRange);

  useEffect(() => {
    setTempRange(dateRange);
  }, [dateRange]);

  const handleApply = () => {
    setDateRange(tempRange);
    onApply(tempRange);
    onClose();
  };

  const handlePresetRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    
    const newRange = {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
    
    setTempRange(newRange);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[5500]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md z-[6000]" dir="rtl">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-yellow-400" />
              تحديد نطاق التاريخ
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Preset Ranges */}
            <div>
              <h4 className="text-white font-medium mb-3">نطاقات سريعة</h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handlePresetRange(7)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  آخر 7 أيام
                </button>
                <button
                  onClick={() => handlePresetRange(30)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  آخر 30 يوم
                </button>
                <button
                  onClick={() => handlePresetRange(90)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  آخر 90 يوم
                </button>
                <button
                  onClick={() => handlePresetRange(365)}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  آخر سنة
                </button>
              </div>
            </div>

            {/* Custom Range */}
            <div className="space-y-4">
              <h4 className="text-white font-medium">نطاق مخصص</h4>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">من تاريخ</label>
                <input
                  type="date"
                  value={tempRange.start}
                  onChange={(e) => setTempRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">إلى تاريخ</label>
                <input
                  type="date"
                  value={tempRange.end}
                  onChange={(e) => setTempRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-slate-700">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors font-medium"
            >
              إلغاء
            </button>
            <button
              onClick={handleApply}
              className="flex-1 px-4 py-3 bg-yellow-400 hover:bg-yellow-500 text-black rounded-xl transition-colors font-bold"
            >
              تطبيق
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Export Modal Component
const ExportModal = ({ isOpen, onClose, data, productName }) => {
  const [exportType, setExportType] = useState('csv');
  const [isExporting, setIsExporting] = useState(false);

  const exportToCSV = () => {
    if (!data) return;

    const { summary, agents_performance, top_users, time_series_data } = data;
    
    let csvContent = `تقرير تحليلات المنتج: ${productName}\n`;
    csvContent += `فترة التقرير: ${data.report_period.start_date} إلى ${data.report_period.end_date}\n\n`;
    
    csvContent += `الملخص التنفيذي\n`;
    csvContent += `إجمالي الطلبات,${summary.total_orders}\n`;
    csvContent += `إجمالي الإيرادات,$${parseFloat(summary.total_revenue).toFixed(2)}\n`;
    csvContent += `إجمالي الكمية,${summary.total_quantity}\n`;
    csvContent += `متوسط هامش الربح,${(parseFloat(summary.avg_profit_margin) * 100).toFixed(2)}%\n`;
    csvContent += `عدد الوكلاء,${summary.unique_agents_count}\n`;
    csvContent += `عدد العملاء,${summary.unique_users_count}\n\n`;

    csvContent += `أداء الوكلاء\n`;
    csvContent += `اسم الوكيل,الطلبات,الكمية,الإيرادات,هامش الربح,العملاء الفريدين\n`;
    agents_performance.forEach(agent => {
      csvContent += `${agent.agent_name},${agent.total_orders},${agent.total_quantity},$${parseFloat(agent.total_revenue).toFixed(2)},${agent.profit_percentage}%,${agent.unique_users}\n`;
    });

    csvContent += `\nأفضل العملاء\n`;
    csvContent += `اسم المستخدم,عدد الطلبات,إجمالي المبلغ,الكمية,متوسط الطلب\n`;
    top_users.forEach(user => {
      csvContent += `${user.username},${user.order_count},$${parseFloat(user.total_spent).toFixed(2)},${user.total_quantity},$${parseFloat(user.avg_order_value).toFixed(2)}\n`;
    });

    csvContent += `\nالبيانات اليومية\n`;
    csvContent += `التاريخ,عدد الطلبات,الكمية المباعة,الإيرادات,متوسط هامش الربح,عدد الوكلاء\n`;
    time_series_data.forEach(day => {
      csvContent += `${day.date},${day.orders_count},${day.quantity_sold},$${parseFloat(day.revenue).toFixed(2)},${(parseFloat(day.avg_profit_margin) * 100).toFixed(2)}%,${day.unique_agents}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${productName}_analytics_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = () => {
    if (!data) return;

    const jsonData = {
      product_name: productName,
      report_date: new Date().toISOString(),
      period: data.report_period,
      analytics: data
    };

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${productName}_analytics_${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (exportType === 'csv') {
        exportToCSV();
      } else if (exportType === 'json') {
        exportToJSON();
      }
      
      onClose();
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[5500]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md z-[6000]" dir="rtl">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-green-400" />
              تصدير التقرير
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h4 className="text-white font-medium mb-3">نوع التصدير</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl cursor-pointer hover:bg-slate-700/70 transition-colors">
                  <input
                    type="radio"
                    name="exportType"
                    value="csv"
                    checked={exportType === 'csv'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="text-yellow-400 focus:ring-yellow-400"
                  />
                  <FileSpreadsheet className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-white font-medium">CSV</p>
                    <p className="text-gray-400 text-sm">ملف Excel قابل للقراءة</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl cursor-pointer hover:bg-slate-700/70 transition-colors">
                  <input
                    type="radio"
                    name="exportType"
                    value="json"
                    checked={exportType === 'json'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="text-yellow-400 focus:ring-yellow-400"
                  />
                  <FileText className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">JSON</p>
                    <p className="text-gray-400 text-sm">بيانات منظمة للمطورين</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="bg-slate-700/30 p-4 rounded-xl">
              <h5 className="text-white font-medium text-sm mb-2">محتويات التقرير:</h5>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• الملخص التنفيذي</li>
                <li>• أداء الوكلاء</li>
                <li>• أفضل العملاء</li>
                <li>• البيانات اليومية</li>
                <li>• إحصائيات الطلبات</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-slate-700">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors font-medium disabled:opacity-50"
            >
              إلغاء
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  جاري التصدير...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  تصدير
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500/10 to-blue-600/10 border-blue-500/20",
    green: "from-green-500/10 to-green-600/10 border-green-500/20",
    yellow: "from-yellow-500/10 to-yellow-600/10 border-yellow-500/20",
    red: "from-red-500/10 to-red-600/10 border-red-500/20",
    purple: "from-purple-500/10 to-purple-600/10 border-purple-500/20",
  };

  const iconColors = {
    blue: "text-blue-400",
    green: "text-green-400",
    yellow: "text-yellow-400",
    red: "text-red-400",
    purple: "text-purple-400",
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} backdrop-blur-sm p-4 sm:p-6 rounded-xl border hover:scale-[1.02] transition-all duration-300`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 bg-slate-700/50 rounded-lg ${iconColors[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            trend === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div>
        <h3 className="text-gray-400 text-sm font-medium mb-1">{title}</h3>
        <p className="text-white text-xl sm:text-2xl font-bold mb-1">{value}</p>
        {subtitle && <p className="text-gray-500 text-xs">{subtitle}</p>}
      </div>
    </div>
  );
};

// Agent Card Component
const AgentCard = ({ agent, index }) => (
  <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-4 sm:p-6 rounded-xl border border-slate-600/30 hover:border-slate-500/50 transition-all duration-300">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-slate-600'
        }`}>
          {index + 1}
        </div>
        <div>
          <h3 className="text-white font-semibold">{agent.agent_name}</h3>
          <p className="text-gray-400 text-sm">{agent.unique_users} مستخدم فريد</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-white font-bold">${parseFloat(agent.total_revenue).toFixed(2)}</p>
        <p className={`text-sm font-medium ${
          parseFloat(agent.profit_percentage) > 2 ? 'text-green-400' : 'text-yellow-400'
        }`}>
          ربح {agent.profit_percentage}%
        </p>
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <span className="text-gray-400 block">الطلبات</span>
        <span className="text-white font-medium">{agent.total_orders.toLocaleString()}</span>
      </div>
      <div>
        <span className="text-gray-400 block">الكمية</span>
        <span className="text-white font-medium">{agent.total_quantity.toLocaleString()}</span>
      </div>
      <div>
        <span className="text-gray-400 block">متوسط السعر</span>
        <span className="text-white font-medium">${parseFloat(agent.avg_price).toFixed(2)}</span>
      </div>
      <div>
        <span className="text-gray-400 block">إجمالي الربح</span>
        <span className="text-white font-medium">${parseFloat(agent.total_profit).toFixed(2)}</span>
      </div>
    </div>
  </div>
);

// User Card Component
const UserCard = ({ user, index }) => (
  <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-4 rounded-xl border border-slate-600/30 hover:border-slate-500/50 transition-all duration-300">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
          index < 3 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 'bg-slate-600'
        }`}>
          {index + 1}
        </div>
        <div>
          <h4 className="text-white font-medium">{user.username}</h4>
          <p className="text-gray-400 text-sm">ID: {user.user_id}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-white font-bold">${parseFloat(user.total_spent).toFixed(2)}</p>
        <p className="text-gray-400 text-sm">{user.order_count} طلب</p>
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <span className="text-gray-400 block">الكمية الإجمالية</span>
        <span className="text-white font-medium">{user.total_quantity.toLocaleString()}</span>
      </div>
      <div>
        <span className="text-gray-400 block">متوسط الطلب</span>
        <span className="text-white font-medium">${parseFloat(user.avg_order_value).toFixed(2)}</span>
      </div>
    </div>
  </div>
);

// Time Series Chart Component
const TimeSeriesChart = ({ data }) => {
  const [viewMode, setViewMode] = useState('all');
  const [chartType, setChartType] = useState('bar');
  const [showMetric, setShowMetric] = useState({
    revenue: true,
    orders: true,
    quantity: false,
    profit: false
  });

  const getDisplayData = () => {
    let processedData = data.map(day => ({
      ...day,
      date: new Date(day.date).toLocaleDateString('ar-EG', { 
        month: 'short', 
        day: 'numeric' 
      }),
      fullDate: day.date,
      revenue: parseFloat(day.revenue),
      profit_margin: parseFloat(day.avg_profit_margin) * 100,
      orders_count: day.orders_count,
      quantity_sold: day.quantity_sold / 1000
    }));

    switch (viewMode) {
      case 'week':
        return processedData.slice(-7);
      case 'month':
        return processedData.slice(-30);
      default:
        return processedData;
    }
  };

  const displayData = getDisplayData();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-4 shadow-2xl">
          <p className="text-white font-semibold mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-300 text-sm">
                  {entry.name}: {
                    entry.dataKey === 'revenue' ? `$${entry.value.toFixed(2)}` :
                    entry.dataKey === 'quantity_sold' ? `${(entry.value * 1000).toLocaleString()} وحدة` :
                    entry.dataKey === 'profit_margin' ? `${entry.value.toFixed(1)}%` :
                    entry.value.toLocaleString()
                  }
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-600 mt-2 pt-2 text-xs text-gray-400">
            <p>وكلاء فريدين: {data.unique_agents}</p>
            <p>التاريخ الكامل: {new Date(data.fullDate).toLocaleDateString('ar-EG')}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const chartColors = {
    revenue: '#3B82F6',
    orders: '#10B981',
    quantity: '#F59E0B',
    profit: '#8B5CF6'
  };

  const renderChart = () => {
    const commonProps = {
      width: '100%',
      height: 300,
      data: displayData,
      margin: { top: 20, right: 30, left: 20, bottom: 60 }
    };

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer {...commonProps}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                fontSize={12}
                angle={displayData.length > 15 ? -45 : 0}
                textAnchor={displayData.length > 15 ? 'end' : 'middle'}
                height={displayData.length > 15 ? 80 : 60}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              {showMetric.revenue && (
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke={chartColors.revenue}
                  strokeWidth={3}
                  dot={{ fill: chartColors.revenue, strokeWidth: 2, r: 4 }}
                  name="الإيرادات"
                />
              )}
              {showMetric.orders && (
                <Line 
                  type="monotone" 
                  dataKey="orders_count" 
                  stroke={chartColors.orders}
                  strokeWidth={2}
                  dot={{ fill: chartColors.orders, strokeWidth: 2, r: 3 }}
                  yAxisId="right"
                  name="الطلبات"
                />
              )}
              {showMetric.quantity && (
                <Line 
                  type="monotone" 
                  dataKey="quantity_sold" 
                  stroke={chartColors.quantity}
                  strokeWidth={2}
                  dot={{ fill: chartColors.quantity, strokeWidth: 2, r: 3 }}
                  name="الكمية (آلاف)"
                />
              )}
              {showMetric.profit && (
                <Line 
                  type="monotone" 
                  dataKey="profit_margin" 
                  stroke={chartColors.profit}
                  strokeWidth={2}
                  dot={{ fill: chartColors.profit, strokeWidth: 2, r: 3 }}
                  yAxisId="right"
                  name="هامش الربح %"
                />
              )}
              <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" fontSize={12} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                fontSize={12}
                angle={displayData.length > 15 ? -45 : 0}
                textAnchor={displayData.length > 15 ? 'end' : 'middle'}
                height={displayData.length > 15 ? 80 : 60}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              {showMetric.revenue && (
                <Bar dataKey="revenue" fill={chartColors.revenue} name="الإيرادات" />
              )}
              {showMetric.orders && (
                <Bar dataKey="orders_count" fill={chartColors.orders} name="الطلبات" />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <ResponsiveContainer {...commonProps}>
            <AreaChart {...commonProps}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.revenue} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={chartColors.revenue} stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.orders} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={chartColors.orders} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                fontSize={12}
                angle={displayData.length > 15 ? -45 : 0}
                textAnchor={displayData.length > 15 ? 'end' : 'middle'}
                height={displayData.length > 15 ? 80 : 60}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              {showMetric.revenue && (
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={chartColors.revenue}
                  fillOpacity={1}
                  fill="url(#revenueGradient)"
                  strokeWidth={2}
                  name="الإيرادات"
                />
              )}
              {showMetric.orders && (
                <Area
                  type="monotone"
                  dataKey="orders_count"
                  stroke={chartColors.orders}
                  fillOpacity={1}
                  fill="url(#ordersGradient)"
                  strokeWidth={2}
                  yAxisId="right"
                  name="الطلبات"
                />
              )}
              <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" fontSize={12} />
            </AreaChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Chart Controls */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">عرض:</span>
          {[
            { key: 'all', label: `كل الفترة (${data.length})`, icon: Eye }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setViewMode(key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === key 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">نوع الرسم:</span>
          {[
            { key: 'area', label: 'مساحة', icon: Activity },
            { key: 'line', label: 'خط', icon: TrendingUp },
            { key: 'bar', label: 'أعمدة', icon: BarChart3 }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setChartType(key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                chartType === key 
                  ? 'bg-green-500 text-white' 
                  : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Metric Toggles */}
      <div className="flex flex-wrap gap-3 p-4 bg-slate-800/50 rounded-xl">
        <span className="text-gray-400 text-sm">المقاييس المعروضة:</span>
        {[
          { key: 'revenue', label: 'الإيرادات', color: chartColors.revenue },
          { key: 'orders', label: 'الطلبات', color: chartColors.orders },
          { key: 'quantity', label: 'الكمية', color: chartColors.quantity }
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setShowMetric(prev => ({ ...prev, [key]: !prev[key] }))}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
              showMetric[key] 
                ? 'border-current text-white' 
                : 'border-slate-600 text-gray-400 hover:text-gray-300'
            }`}
            style={{ borderColor: showMetric[key] ? color : undefined }}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: color, opacity: showMetric[key] ? 1 : 0.3 }}
            />
            {label}
            {showMetric[key] ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-slate-800/30 rounded-xl p-4">
        {renderChart()}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-600">
        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
          <p className="text-gray-400 text-xs mb-1">متوسط يومي</p>
          <p className="text-white font-bold text-lg">
            ${(displayData.reduce((sum, day) => sum + day.revenue, 0) / displayData.length).toFixed(2)}
          </p>
          <p className="text-gray-500 text-xs">إيرادات</p>
        </div>
        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
          <p className="text-gray-400 text-xs mb-1">أعلى يوم</p>
          <p className="text-white font-bold text-lg">
            ${Math.max(...displayData.map(d => d.revenue)).toFixed(2)}
          </p>
          <p className="text-gray-500 text-xs">أفضل أداء</p>
        </div>
        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
          <p className="text-gray-400 text-xs mb-1">إجمالي الطلبات</p>
          <p className="text-white font-bold text-lg">
            {displayData.reduce((sum, day) => sum + day.orders_count, 0).toLocaleString()}
          </p>
          <p className="text-gray-500 text-xs">طلب</p>
        </div>
        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
          <p className="text-gray-400 text-xs mb-1">متوسط الربح</p>
          <p className="text-white font-bold text-lg">
            {(displayData.reduce((sum, day) => sum + day.profit_margin, 0) / displayData.length).toFixed(1)}%
          </p>
          <p className="text-gray-500 text-xs">هامش</p>
        </div>
      </div>
    </div>
  );
};

// Main Popup Component
const ProductAnalyticsPopup = ({ isOpen, onClose, productId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [showDateModal, setShowDateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    if (isOpen && productId) {
      fetchAnalytics();
    }
  }, [isOpen, productId]);

  const fetchAnalytics = async (customRange = null) => {
    try {
      setLoading(true);
      const rangeToUse = customRange || dateRange;
      
      const response = await axios.get(
        `https://dailycard.co/api/dashboard/Products/${productId}/financial-report/`,
        {
          params: {
            start_date: rangeToUse.start,
            end_date: rangeToUse.end,
            group_by: 'day'
          },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      setData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeApply = (newRange) => {
    setDateRange(newRange);
    fetchAnalytics(newRange);
  };

  const formatDateInArabic = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', { 
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[5000]" onClick={onClose} />

      {/* Main Popup */}
      <div className="fixed inset-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 z-[5500] rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden" dir="rtl">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Header */}
        <div className="relative bg-gray-900/90 backdrop-blur-sm border-b border-gray-700/50 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-white truncate">تحليلات المنتج</h2>
                {data && (
                  <p className="text-xs sm:text-sm text-gray-400 flex items-center gap-2">
                    <Package className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span className="truncate">{data.product.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                      data.product.status === 'available' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {data.product.status === 'available' ? 'متوفر' : 'غير متوفر'}
                    </span>
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {!loading && !error && (
                <>
                  <button 
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg sm:rounded-xl transition-colors text-xs sm:text-sm font-medium"
                  >
                    <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">تصدير</span>
                  </button>

                  <button 
                    onClick={() => setShowDateModal(true)}
                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg sm:rounded-xl transition-colors text-xs sm:text-sm font-medium whitespace-nowrap"
                  >
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="hidden lg:inline">
                      الفترة: {formatDateInArabic(dateRange.start)} - {formatDateInArabic(dateRange.end)}
                    </span>
                    <span className="lg:hidden">
                      {formatDateInArabic(dateRange.start)} - {formatDateInArabic(dateRange.end)}
                    </span>
                  </button>
                </>
              )}

              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 hover:bg-red-500/20 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 hover:text-red-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative h-[calc(100%-70px)] sm:h-[calc(100%-80px)] overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 animate-spin" />
              <p className="text-white text-base sm:text-lg">جاري تحميل التحليلات...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full">
              <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-red-400 mb-4" />
              <p className="text-white text-lg sm:text-xl mb-2">خطأ في تحميل التحليلات</p>
              <p className="text-gray-400 text-sm sm:text-base">{error}</p>
            </div>
          ) : data ? (
            <>
              {/* Main Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatsCard
                  title="إجمالي الطلبات"
                  value={data.summary.total_orders.toLocaleString()}
                  subtitle={`${data.summary.unique_users_count} مستخدم`}
                  icon={ShoppingCart}
                  color="blue"
                />
                <StatsCard
                  title="إجمالي الإيرادات"
                  value={`$${parseFloat(data.summary.total_revenue).toFixed(2)}`}
                  subtitle={`متوسط: $${parseFloat(data.summary.avg_order_value).toFixed(2)}`}
                  icon={DollarSign}
                  color="green"
                />
                <StatsCard
                  title="إجمالي الكمية"
                  value={data.summary.total_quantity.toLocaleString()}
                  subtitle="وحدة مباعة"
                  icon={Package}
                  color="yellow"
                />
                <StatsCard
                  title="الربح"
                  value={`$${parseFloat(data.summary.total_profit_margin_sum).toFixed(2)}`}
                  icon={TrendingUp}
                  color="purple"
                />
              </div>

              {/* Product Info */}
              <div className="bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-600/50 shadow-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                  معلومات المنتج
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-slate-800 p-3 sm:p-4 rounded-xl">
                    <h4 className="text-gray-400 text-xs sm:text-sm mb-1">اسم المنتج</h4>
                    <p className="text-white text-sm sm:text-base font-semibold truncate">{data.product.name}</p>
                  </div>
                  <div className="bg-slate-800 p-3 sm:p-4 rounded-xl">
                    <h4 className="text-gray-400 text-xs sm:text-sm mb-1">السعر الحالي</h4>
                    <p className="text-white text-sm sm:text-base font-semibold">${parseFloat(data.product.current_price).toFixed(4)}</p>
                  </div>
                  <div className="bg-slate-800 p-3 sm:p-4 rounded-xl">
                    <h4 className="text-gray-400 text-xs sm:text-sm mb-1">نوع المنتج</h4>
                    <p className="text-white text-sm sm:text-base font-semibold">{data.product.product_type}</p>
                  </div>
                  <div className="bg-slate-800 p-3 sm:p-4 rounded-xl">
                    <h4 className="text-gray-400 text-xs sm:text-sm mb-1">معدل العمولة</h4>
                    <p className="text-white text-sm sm:text-base font-semibold">{data.product.commission_rate}%</p>
                  </div>
                </div>
              </div>

              {/* Chart */}
              {data.time_series_data && data.time_series_data.length > 0 && (
                <div className="bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-600/50 shadow-2xl p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      تحليل الأداء اليومي
                    </h3>
                    <div className="text-xs sm:text-sm text-gray-400 flex items-center gap-2">
                      <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
                      {data.time_series_data.length} يوم
                    </div>
                  </div>
                  <TimeSeriesChart 
                    key={`${dateRange.start}-${dateRange.end}-${data.time_series_data.length}`}
                    data={data.time_series_data} 
                  />
                </div>
              )}

              {/* Agents & Users */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-600/50 shadow-2xl p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                      أداء الوكلاء
                    </h3>
                    <span className="text-xs sm:text-sm text-gray-400">{data.summary.unique_agents_count} وكلاء</span>
                  </div>
                  <div className="space-y-3 sm:space-y-4 max-h-80 sm:max-h-96 overflow-y-auto custom-scrollbar">
                    {data.agents_performance.map((agent, index) => (
                      <AgentCard key={agent.agent_name} agent={agent} index={index} />
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-600/50 shadow-2xl p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                      <Award className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                      أفضل العملاء
                    </h3>
                    <span className="text-xs sm:text-sm text-gray-400">أعلى 10</span>
                  </div>
                  <div className="space-y-2 sm:space-y-3 max-h-80 sm:max-h-96 overflow-y-auto custom-scrollbar">
                    {data.top_users.map((user, index) => (
                      <UserCard key={user.user_id} user={user} index={index} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Status */}
              <div className="bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-600/50 shadow-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500" />
                  إحصائيات الطلبات
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 p-3 sm:p-4 rounded-xl">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-xs sm:text-sm">طلبات مكتملة</span>
                    </div>
                    <p className="text-white text-xl sm:text-2xl font-bold">{data.order_status_breakdown.completed.toLocaleString()}</p>
                    <p className="text-green-400 text-xs sm:text-sm">{((data.order_status_breakdown.completed / data.summary.total_orders) * 100).toFixed(1)}%</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-500/10 to-red-600/10 border border-red-500/20 p-3 sm:p-4 rounded-xl">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <span className="text-gray-300 text-xs sm:text-sm">طلبات ملغية</span>
                    </div>
                    <p className="text-white text-xl sm:text-2xl font-bold">{data.order_status_breakdown.cancelled.toLocaleString()}</p>
                    <p className="text-red-400 text-xs sm:text-sm">{((data.order_status_breakdown.cancelled / data.summary.total_orders) * 100).toFixed(1)}%</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 p-3 sm:p-4 rounded-xl">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                      <span className="text-gray-300 text-xs sm:text-sm">معدل النجاح</span>
                    </div>
                    <p className="text-white text-xl sm:text-2xl font-bold">{((data.order_status_breakdown.completed / data.summary.total_orders) * 100).toFixed(1)}%</p>
                    <p className="text-blue-400 text-xs sm:text-sm">من إجمالي الطلبات</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border border-yellow-500/20 p-3 sm:p-4 rounded-xl">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <span className="text-gray-300 text-xs sm:text-sm">متوسط يومي</span>
                    </div>
                    <p className="text-white text-xl sm:text-2xl font-bold">{(data.summary.total_orders / data.time_series_data.length).toFixed(0)}</p>
                    <p className="text-yellow-400 text-xs sm:text-sm">طلب/يوم</p>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Modals */}
      {data && (
        <>
          <DateRangeModal
            isOpen={showDateModal}
            onClose={() => setShowDateModal(false)}
            dateRange={dateRange}
            setDateRange={setDateRange}
            onApply={handleDateRangeApply}
          />

          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            data={data}
            productName={data.product.name}
          />
        </>
      )}

      {/* Custom Scrollbar */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(71, 85, 105, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(250, 204, 21, 0.6);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(250, 204, 21, 0.8);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(250, 204, 21, 0.6) rgba(71, 85, 105, 0.3);
        }
      `}</style>
    </>
  );
};

export default ProductAnalyticsPopup;